import { useReducer } from "react";
import { mergeObjects } from ".";

type Field = string | number;
type Data = Record<string, unknown> | unknown[];
type DataAction =
  | { type: "update" | "updateField"; field?: Field; update: unknown }
  | { type: "deleteField"; field: Field };
type UseDataProps = {
  initialData?: Data;
  field?: Field;
  dispatchData?: (action: DataAction) => void;
};

const useData = (props: UseDataProps = {}) => {
  const dataReducer = (state: Data, action: DataAction): Data => {
    switch (action.type) {
      case "update": {
        if (action.field) {
          return { ...state, ...{ [action.field]: action.update } };
        }
        return action.update as Data;
      }
      case "updateField": {
        let update;
        if (Array.isArray((state as Record<Field, unknown>)[action.field as Field])) {
          // if the parent state is an array, then the field  on the update is the index position of the array
          update = new Array();
          update[action.field as number] = action.update;
        } else {
          const previous = (state as Record<Field, unknown>)[action.field as Field] || {};
          const updated =
            typeof action.update === "object"
              ? { ...(previous as object), ...action.update }
              : action.update;
          update = { [action.field as Field]: updated };
        }
        const updatedState = mergeObjects(state, update) as Data;
        if (props.dispatchData) {
          props.dispatchData({
            type: "updateField",
            field: props.field,
            update: updatedState,
          });
        }
        return updatedState;
      }
      case "deleteField": {
        const copy = { ...state };
        delete (copy as Record<Field, unknown>)[action.field];
        if (props.dispatchData) {
          props.dispatchData({
            type: "update",
            field: props.field,
            update: copy,
          });
        }
        return copy;
      }
      default:
        throw new Error();
    }
  };
  const [data, dispatchData] = useReducer(dataReducer, props.initialData || {});
  return { data, dispatchData };
};

export default useData;
